import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { useUserState } from 'src/contexts/UserContext';
import { onboardingSurveyTypeformId } from './Welcome.const';

interface TypeformWidget {
  unmount: () => void;
}

interface TypeformWidgetOptions {
  container: HTMLElement;
  hidden?: Record<string, string>;
  opacity?: number;
  inlineOnMobile?: boolean;
  iframeProps?: Record<string, string>;
  onSubmit?: () => void;
}

declare global {
  interface Window {
    tf?: {
      createWidget: (formId: string, options: TypeformWidgetOptions) => TypeformWidget;
    };
  }
}

const typeformEmbedUrl = 'https://embed.typeform.com/next/embed.js';
const typeformWidgetCssUrl = 'https://embed.typeform.com/next/css/widget.css';
// Leaves time to read the ending screen before the dialog closes.
const closeAfterSubmitMs = 3000;

const loadTypeformEmbed = () => new Promise<void>((resolve, reject) => {
  if (window.tf) {
    resolve();
    return;
  }
  if (!document.querySelector(`link[href="${typeformWidgetCssUrl}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = typeformWidgetCssUrl;
    document.head.appendChild(link);
  }
  let script = document.querySelector<HTMLScriptElement>(`script[src="${typeformEmbedUrl}"]`);
  if (!script) {
    script = document.createElement('script');
    script.src = typeformEmbedUrl;
    script.async = true;
    document.body.appendChild(script);
  }
  script.addEventListener('load', () => resolve());
  script.addEventListener('error', reject);
});

/**
 * Required onboarding survey: the dialog has no close button and ignores
 * Escape and backdrop clicks, it closes only after the form is submitted.
 */
const OnboardingSurvey: React.FC = () => {
  const { user } = useUserState();
  const { id: userId, email } = user;
  const firstName = user.name.trim().split(' ')[0] ?? '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isOpen, setIsOpen] = useState(false);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTypeformEmbed()
      .then(() => {
        if (!cancelled && window.tf) {
          setIsOpen(true);
        }
      })
      .catch(() => {
        // The survey is optional, the welcome view works without it.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!container || !window.tf) {
      return undefined;
    }
    let closeTimeout: number | undefined;
    const widget = window.tf.createWidget(onboardingSurveyTypeformId, {
      container,
      opacity: 100,
      inlineOnMobile: true,
      iframeProps: { title: 'Witamy w PortalYogi' },
      hidden: {
        first_name: firstName,
        email,
        user_id: String(userId),
      },
      onSubmit: () => {
        closeTimeout = window.setTimeout(() => setIsOpen(false), closeAfterSubmitMs);
      },
    });
    return () => {
      window.clearTimeout(closeTimeout);
      widget.unmount();
    };
  }, [container, userId, email, firstName]);

  return (
    <Dialog
      open={isOpen}
      fullScreen={isMobile}
      maxWidth={false}
      disableEscapeKeyDown
      PaperProps={{
        sx: isMobile ? undefined : { width: '70vw', height: '70vh', maxHeight: 'none' },
      }}
    >
      <div ref={setContainer} style={{ width: '100%', height: '100%' }} />
    </Dialog>
  );
};

export default OnboardingSurvey;
